import type { Uuid } from "spacetimedb";
import { CheckIcon, ChevronRightIcon } from "lucide-react";
import { useEffect, useState } from "react";

import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { formatCanonicalAddressShort } from "@/web/lib/addresses";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createReviewCompsCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/ReviewCompsCompleted";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";
import type { Property } from "@tycho/web-bindings/types";
import { CompsReviewPanel } from "./CompsReviewPanel";
import { useCompsReviewFlow } from "./useCompsReviewFlow";

type ReviewCompsCardProps = {
  underwritableEntity: Uuid;
  /** Every targeted property that has comps (reviewed or not). */
  properties: Property[];
};

/** A property is reviewed once it has a selected top-three. */
const isReviewed = (p: Property) => (p.selectedCompIds?.length ?? 0) > 0;

function ReviewCompsAction({
  underwritableEntity,
  properties,
  onSkip,
}: ReviewCompsCardProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);
  const remaining = properties.filter((p) => !isReviewed(p)).length;
  const multiple = properties.length > 1;

  return (
    <>
      <ActionCard category="negotiate">
        <ActionCard.Header>
          <ActionCard.Badge>Price</ActionCard.Badge>
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title className="max-w-72">Review comparable properties</ActionCard.Title>
          <ActionCard.Description className="space-y-4">
            {multiple ? (
              <p>
                You're the local expert — pick the most similar buildings for each of your{" "}
                <strong>{properties.length}</strong> properties ({remaining} left). Think location,
                size, and use.
              </p>
            ) : (
              <p>
                You're the local expert — pick the most similar buildings to your property. Think
                location, size, and use.
              </p>
            )}
            <p>We'll use your answers to help determine the price for each property.</p>
            <p>
              Note that the properties shown are <strong>not necessarily available</strong> for sale
              or lease.
            </p>
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>Review</ActionCard.PrimaryButton>
        </ActionCard.Footer>
      </ActionCard>

      {open ? (
        <ReviewCompsDrawer
          underwritableEntity={underwritableEntity}
          properties={properties}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Free-order comps review. The drawer opens to a picker listing every targeted
 * property: unreviewed ones are tappable, already-reviewed ones show a "Done"
 * badge and are locked. Tapping a property runs its comps exercise, then returns
 * to the picker so the user can do the rest in any order.
 *
 * The card is NOT removed from the deck on completion — finishing a property's
 * review only flips its `selectedCompIds` server-side. We wait for that to
 * propagate and let the rule predicate drop the card once *every* property is
 * reviewed (the engine already encodes "show while any property is unreviewed").
 * Calling the deck's `onComplete` here would hide the card after the *first*
 * property, which is wrong for the multi-property flow.
 *
 * With a single property we skip the picker and open its exercise directly.
 */
function ReviewCompsDrawer({
  underwritableEntity,
  properties,
  onClose,
}: {
  underwritableEntity: Uuid;
  properties: Property[];
  onClose: () => void;
}) {
  const single = properties.length === 1;
  // The property whose exercise is open, or null for the picker. For a single
  // property, start directly in its exercise.
  const [activeId, setActiveId] = useState<string | null>(
    single ? (properties[0]?.id.toString() ?? null) : null,
  );
  // Reviews completed this session — drives the picker's done state without
  // waiting on the live subscription to flip `selectedCompIds`.
  const [reviewedThisSession, setReviewedThisSession] = useState<ReadonlySet<string>>(new Set());
  // The active exercise reports `true` while swiping so the (single, shared)
  // Drawer can switch to `handleOnly` — drag-to-dismiss must not fight card
  // swipes. Lives here because the stage is owned by the exercise's hook, but
  // the prop belongs to the Drawer that wraps both panes.
  const [swiping, setSwiping] = useState(false);

  const active = activeId ? properties.find((p) => p.id.toString() === activeId) : undefined;

  const markReviewed = (id: Uuid) =>
    setReviewedThisSession((prev) => {
      const next = new Set(prev);
      next.add(id.toString());
      return next;
    });

  // Dismissing the whole drawer (X / drag) always closes the card. From the
  // picker that's the only exit; from an exercise we also reset its session via
  // the exercise's own cleanup on unmount.
  const handleOpenChange = (next: boolean) => {
    if (!next) onClose();
  };

  return (
    <Drawer open onOpenChange={handleOpenChange} handleOnly={swiping}>
      <DrawerContent className="bg-withus-white">
        {active ? (
          // One Drawer, swapped content: the keyed exercise replaces the picker
          // in place (no close/open animation between panes).
          <ReviewCompsExercise
            key={active.id.toString()}
            underwritableEntity={underwritableEntity}
            property={active}
            onSwipingChange={setSwiping}
            onSubmitted={() => {
              markReviewed(active.id);
              setSwiping(false);
              // Don't dismiss the card here — just close (single) or return to
              // the picker (multi). The engine drops the card from the deck on
              // its own once the last property's review propagates.
              if (single) onClose();
              else setActiveId(null); // back to the picker
            }}
            // The in-panel back affordance returns to the picker (or closes the
            // drawer when there's only the one property).
            onBack={
              single
                ? onClose
                : () => {
                    setSwiping(false);
                    setActiveId(null);
                  }
            }
          />
        ) : (
          <>
            <DrawerHeader>
              <span className="size-5" aria-hidden />
              <DrawerTitle>Review comparable properties</DrawerTitle>
              <DrawerCloseIcon onClick={onClose} />
            </DrawerHeader>
            <DrawerBody className="flex flex-col gap-3 py-6">
              <p className="text-sm text-muted-foreground">
                Choose a property to review its comparables. You can do these in any order.
              </p>
              <ul className="flex flex-col gap-2">
                {properties.map((p) => {
                  const done = isReviewed(p) || reviewedThisSession.has(p.id.toString());
                  return (
                    <li key={p.id.toString()}>
                      <button
                        type="button"
                        disabled={done}
                        onClick={() => setActiveId(p.id.toString())}
                        className="flex w-full items-center justify-between border border-border p-4 text-left transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <span className="font-heading text-base uppercase">
                          {formatCanonicalAddressShort(p.address)}
                        </span>
                        {done ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold tracking-wide text-withus-dark-blue/60 uppercase">
                            <CheckIcon className="size-4" />
                            Done
                          </span>
                        ) : (
                          <ChevronRightIcon className="size-4 text-withus-dark-blue/60" />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </DrawerBody>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}

/**
 * One property's comps exercise, rendered as *content* inside the shared Drawer
 * (it does not own a Drawer of its own). Owns the per-property review hook and
 * reports its swipe stage up so the parent Drawer can toggle `handleOnly`.
 */
function ReviewCompsExercise({
  underwritableEntity,
  property,
  onSubmitted,
  onBack,
  onSwipingChange,
}: {
  underwritableEntity: Uuid;
  property: Property;
  onSubmitted: () => void;
  onBack: () => void;
  onSwipingChange: (swiping: boolean) => void;
}) {
  const flow = useCompsReviewFlow({
    underwritableEntity,
    property,
    onSubmitSuccess: () => {
      flow.resetCompsSession();
      onSubmitted();
    },
  });

  // Mirror the swipe stage up to the parent Drawer's `handleOnly`, and reset it
  // on unmount so the picker isn't left in handle-only mode.
  const swiping = flow.stage.current === "swipe";
  useEffect(() => {
    onSwipingChange(swiping);
  }, [swiping, onSwipingChange]);
  useEffect(() => () => onSwipingChange(false), [onSwipingChange]);

  return <CompsReviewPanel flow={flow} onClose={onBack} />;
}

export function createReviewCompsAction(props: {
  underwritableEntity: Uuid;
  properties: Property[];
  id?: string;
  canSkip?: boolean;
}): SmbActionDefinition {
  const { underwritableEntity, properties, id = "review-comps", canSkip = true } = props;
  return {
    id,
    category: "negotiate",
    canSkip,
    render: (core) => (
      <ReviewCompsAction
        underwritableEntity={underwritableEntity}
        properties={properties}
        {...core}
      />
    ),
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────

// Single card across every targeted property: shows when ANY property has
// unreviewed comps. Its drawer is a free-order picker over every property that
// has comps (reviewed ones shown done + locked). Leaves the deck once all are
// reviewed.
export const reviewCompsRule = CardRulesEngine.singletonRule({
  id: "review-comps",
  priority: 95,
  when: (d) => d.properties.some((p) => CardRulesEngine.propertyHasUnreviewedComps(d, p)),
  build: (d) =>
    createReviewCompsAction({
      underwritableEntity: d.underwritableEntityId,
      properties: d.properties.filter((p) => p.comps.length > 0),
    }),
});

export const reviewCompsCompletedRule = CardRulesEngine.forEachCompletedRule({
  id: "review-comps:done",
  forEach: CardRulesEngine.property,
  when: CardRulesEngine.propertyHasReviewedComps,
  build: (_, p) => createReviewCompsCompleted({ property: p }),
});
